import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { FormArray, FormControl } from '@angular/forms';
import {
  AutoCompleteData,
  IAutoCompleteData,
} from '../../interfaces/i-auto-complete-data';
import { TagService } from 'src/app/content-management/shared/services/tag.service';
import { ITag, Tag } from 'src/app/core/models/i-tag';
import { TagType } from 'src/app/content-management/shared/enums/tag-type';
import {
  catchError,
  debounceTime,
  distinctUntilChanged,
  filter,
  finalize,
  map,
  switchMap,
  takeUntil,
  tap,
} from 'rxjs/operators';
import { BehaviorSubject, ReplaySubject, Subject, combineLatest, of } from 'rxjs';
import { SearchTagResponse } from 'src/app/core/models/i-search-tag-response';
import { ITopic } from 'src/app/core/models/i-topic';
import { TopicService } from 'src/app/content-management/shared/services/topic.service';
import { CompanyAndPeopleService } from 'src/app/content-management/shared/services/company-and-people.service';
import { ICompanySearchResponse } from 'src/app/core/models/i-company-search-response';
import { Company, ICompany } from 'src/app/core/models/i-company';
import { IPersonSearchResponse } from 'src/app/core/models/i-person-search-response';
import { IPersonSearch } from 'src/app/core/models/i-person-search';
import {
  ISelectListItem,
  SelectListItem,
} from '../../interfaces/i-select-list-item';
import { ITypedSelectListItem } from '../../interfaces/i-typed-select-list-item';
import { DialogService } from 'src/app/shared/services/dialog.service';
import { ICreateTagResponse } from 'src/app/core/models/i-create-tag-response';
import { ILinkInsert } from 'src/app/core/dtos/interfaces/i-link-insert';
import { DataLinkType } from 'src/app/core/dtos/enums/data-link-type';
import { EmailService } from 'src/app/shared/services/email.service';
import { SurtitleTagType } from 'src/app/content-management/shared/enums/surtitle-tag-type';
import {
  ITopicWithTranslations,
  TopicWithTranslations,
} from 'src/app/core/models/i-topic-with-translations';
import { MatSnackBar } from '@angular/material/snack-bar';
import {
  AutoTagKind,
  IAutoTagResult,
  IAutoTagReview,
  IAutoTagReviewOption,
} from 'src/app/core/models/i-auto-tag';
import {
  IAutoTagDecision,
  IAutoTagLink,
} from 'src/app/core/models/i-auto-tag-responses';
import {
  AUTO_TAG_ALREADY_RUNNING_HINT,
  AUTO_TAG_NO_CHANGE_DISPLAY_MS,
  AUTO_TAG_NO_CHANGE_MESSAGE,
  AUTO_TAG_READ_ONLY_HINT,
  AUTO_TAG_READY_HINT,
  AUTO_TAG_RUN_FAILED_MESSAGE,
  AUTO_TAG_SAVE_FIRST_HINT,
  AUTO_TAG_SAVE_IN_PROGRESS_HINT,
  AUTO_TAG_UNAVAILABLE_MESSAGE,
  AutoTagArticleGateway,
  AutoTaggingStateService,
  AutoTagRunErrorReason,
  AutoTagRunStatus,
  IAutoTagRunState,
} from 'src/app/shared/services/auto-tagging-state.service';
import { ExpandableSectionBoxComponent } from '../expandable-section-box/expandable-section-box.component';
import { autoTagContentHash } from 'src/app/shared/utils/auto-tag-content-hash';
import {
  ISmartLinkTrigger,
  SmartLinkTriggersService,
} from 'src/app/shared/services/smart-link-triggers.service';

// Registered with SmartLinkTriggersService so the content status bar's Smart Link button runs auto
// tagging alongside the related-data smart-link scan when both features are enabled.
export const AUTO_TAG_TRIGGER_ID = 'auto-tagging';

@Component({
  selector: 'app-tagging',
  templateUrl: './tagging.component.html',
  styleUrls: ['./tagging.component.scss'],
})
export class TaggingComponent implements OnInit, OnChanges, OnDestroy {
  @Input() isExpandedByDefault = true;
  @Input() isExpandable = true;
  @Input()
  set websites(value: string[]) {
    this._websites = value ?? [];
    this.websites$.next(this._websites);
  }
  get websites(): string[] {
    return this._websites;
  }
  @Input() topicControl: FormArray<FormControl<ITopicWithTranslations>>;
  @Input() tagControl: FormArray<FormControl<ITag>>;
  @Input() companyControl: FormArray<FormControl<ICompany>>;
  @Input() peopleControl: FormArray<FormControl<IPersonSearch>>;
  @Input() isShowingPeopleAndCompanies = true;
  @Input() isShowingSurtitles: boolean = false;
  @Output() handleAddToBody = new EventEmitter<ILinkInsert>();
  @Output() handleRemoveLink = new EventEmitter<{
    regex: string;
    newValue: string;
  }>();
  @Output() handleItemSelected = new EventEmitter<ITypedSelectListItem>();
  @Output() handleItemRemoved = new EventEmitter<ITypedSelectListItem>();
  @Output() handleSurtitlePromoted = new EventEmitter<ITypedSelectListItem>();

  // AI Auto-Tagging (CMS-2565): the run is triggered from this panel's header; confident topic/tag
  // suggestions are auto-added to the lists below, each flagged so search-list shows the AI icon.
  @Input() isAutoTaggingEnabled = false;
  @Input() isEditMode = false;
  @Input() articleId: string | null | undefined;
  @Input() autoTagGateway!: AutoTagArticleGateway;

  autoTagStatus: AutoTagRunStatus = 'idle';
  autoTagErrorReason: AutoTagRunErrorReason | null = null;
  // Ambiguous suggestions the journalist must resolve (shown at the top, marked "needs review").
  autoTagReviewItems: IAutoTagReview[] = [];
  // True when the headline/standfirst/body have been edited since the completed run was tagged, so
  // the suggestions may be stale. Drives the "content changed" info box.
  autoTagContentChanged = false;
  // Briefly shown in the progress box's place when Auto Tag is pressed with unchanged content.
  autoTagNoChangeVisible = false;
  readonly autoTagNoChangeMessage = AUTO_TAG_NO_CHANGE_MESSAGE;
  private autoAddedTopicIds = new Set<string>();
  private autoAddedTagIds = new Set<string>();
  // `${kind}:${id}` -> originating suggestion_key, so a later removal (even after reload) can reject
  // that suggestion. Populated whenever a suggestion is applied or a restored run is re-flagged.
  private autoAddedSuggestionKeys = new Map<string, string>();
  private autoAppliedRunId: string | null = null;
  private taggedContentHash: string | null = null;
  private noChangeTimer: ReturnType<typeof setTimeout> | null = null;

  @ViewChild(ExpandableSectionBoxComponent)
  private sectionBox?: ExpandableSectionBoxComponent;

  isSearchingTopics: boolean = false;
  isSearchingTags: boolean = false;
  isSearchingCompanies: boolean = false;
  isSearchingPeople: boolean = false;
  SurtitleTagType = SurtitleTagType;

  searchTopicsFormControl: FormControl = new FormControl('');
  searchTagsFormControl: FormControl = new FormControl('');
  searchCompaniesFormControl: FormControl = new FormControl('');
  searchPeopleFormControl: FormControl = new FormControl('');

  topicSearchResults: IAutoCompleteData[];
  tagSearchResults: IAutoCompleteData[];
  companySearchResults: IAutoCompleteData[];
  personSearchResults: IAutoCompleteData[];

  topicResults: ITopicWithTranslations[];
  tagResults: ITag[];
  companyResults: ICompany[] = [];
  personResults: IPersonSearch[];

  selectedTopicList: ISelectListItem[] = [];
  selectedTagList: ISelectListItem[] = [];
  selectedCompanyList: ISelectListItem[] = [];
  selectedPersonList: ISelectListItem[] = [];

  destroy$ = new Subject<boolean>();

  private _websites: string[] = [];
  private websites$ = new BehaviorSubject<string[]>([]);
  private allTopics$ = new ReplaySubject<ITopicWithTranslations[]>(1);

  constructor(
    public taggingService: TagService,
    public topicService: TopicService,
    public peopleAndCompanyService: CompanyAndPeopleService,
    public dialogService: DialogService,
    public emailService: EmailService,
    private _snackBar: MatSnackBar,
    public autoTaggingState: AutoTaggingStateService,
    private smartLinkTriggers: SmartLinkTriggersService
  ) {}

  // What the content status bar's Smart Link button runs for auto tagging. One object, so the
  // identity it registers under is the identity it unregisters with. run() re-checks its own guards.
  private readonly autoTagTrigger: ISmartLinkTrigger = {
    isBusy: () => this.isAutoTagBusy,
    canRun: () => this.canRunAutoTag,
    // Unlike smart linking, a run here never carries content — cms-api reads it from the saved
    // article. skipSave means the caller (the status bar button) will never save on our behalf,
    // so a dirty article is declined rather than run against stale, already-saved content.
    run: (options) => {
      if (options?.skipSave && this.autoTagGateway?.isDirty()) {
        return;
      }
      void this.onRunAutoTag();
    },
  };

  ngOnInit() {
    // Topics
    this.selectedTopicList = this.topicControl.value.map((topic: ITopic) =>
      this.buildTopicItem(topic)
    );
    this.topicControl.valueChanges.pipe(takeUntil(this.destroy$)).subscribe({
      next: (x: ITopic[]) => {
        this.selectedTopicList = x.map((topic) => this.buildTopicItem(topic));
      },
    });

    this.topicService
      .getFiltered$('', '')
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (x: ITopicWithTranslations[]) => {
          if (x) {
            this.allTopics$.next(x);
          }
        },
      });

    combineLatest([
      this.allTopics$,
      this.websites$.pipe(
        distinctUntilChanged((prev, curr) => prev.join(',') === curr.join(','))
      ),
    ])
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: ([topics, websites]) => {
          this.topicResults = this.filterTopicsByWebsites(
            topics,
            websites
          ).filter((y) => y.parentId !== null);
          this.topicSearchResults = this.topicResults.map(
            (p: ITopic) => new AutoCompleteData(p.id.toString(), p.displayName)
          );
          this.removeUnavailableTopics(topics, websites);
        },
      });

    this.searchTopicsFormControl.valueChanges
      .pipe(
        distinctUntilChanged(),
        takeUntil(this.destroy$),
        debounceTime(500),
        tap(() => {
          this.isSearchingTopics = true;
        }),
        switchMap((searchedValue: string) => {
          const getDataObs$ = this.topicService.getFiltered$(
            this.websites.join(','),
            searchedValue
          );

          return getDataObs$.pipe(
            finalize(() => {
              this.isSearchingTopics = false;
            })
          );
        })
      )
      .subscribe({
        next: (x: ITopicWithTranslations[]) => {
          if (x) {
            this.topicResults = this.filterTopicsByWebsites(
              x,
              this.websites
            ).filter((y) => y.parentId !== null);
            this.topicSearchResults = this.topicResults.map(
              (p: ITopic) =>
                new AutoCompleteData(p.id.toString(), p.displayName)
            );
          }
        },
      });

    // Tags
    this.selectedTagList = this.tagControl.value.map((tag: ITag) =>
      this.buildTagItem(tag)
    );
    this.tagControl.valueChanges.pipe(takeUntil(this.destroy$)).subscribe({
      next: (x: ITag[]) => {
        this.selectedTagList = x.map((tag) => this.buildTagItem(tag));
      },
    });

    if (this.isAutoTaggingEnabled) {
      this.autoTaggingState.state$
        .pipe(takeUntil(this.destroy$))
        .subscribe((state) => this.onAutoTagState(state));

      this.autoTagGateway
        ?.contentChanges$()
        .pipe(debounceTime(400), takeUntil(this.destroy$))
        .subscribe(() => this.refreshContentChanged());
    }

    this.searchTagsFormControl.valueChanges
      .pipe(
        distinctUntilChanged(),
        takeUntil(this.destroy$),
        debounceTime(500),
        tap(() => {
          this.isSearchingTags = true;
        }),
        switchMap((searchedValue: string) => {
          if (searchedValue === '') {
            this.resetSearchResults();
          }
          const getDataObs$ = searchedValue
            ? this.taggingService.get$(searchedValue, TagType.Soft)
            : of(null);

          return getDataObs$.pipe(
            finalize(() => {
              this.isSearchingTags = false;
            })
          );
        })
      )
      .subscribe({
        next: (x: SearchTagResponse) => {
          if (x) {
            this.tagResults = x.results;
            this.tagSearchResults = x.results.map(
              (p: ITag) => new AutoCompleteData(p.id.toString(), p.text)
            );
          }
        },
      });

    // Companies
    this.selectedCompanyList = this.companyControl.value.map(
      (company: ICompany) => {
        return new SelectListItem(
          company.id.toString(),
          company.name,
          true,
          true
        );
      }
    );

    this.companyControl.valueChanges.pipe(takeUntil(this.destroy$)).subscribe({
      next: (x: ICompany[]) => {
        this.selectedCompanyList = x.map(
          (company) =>
            new SelectListItem(company.id.toString(), company.name, true, true)
        );
      },
    });

    this.searchCompaniesFormControl.valueChanges
      .pipe(
        distinctUntilChanged(),
        debounceTime(500),
        takeUntil(this.destroy$),
        tap(() => {
          this.isSearchingCompanies = true;
        }),
        switchMap((searchedValue: string) => {
          if (searchedValue === '') {
            this.resetSearchResults();
          }
          const getDataObs$ = searchedValue
            ? this.peopleAndCompanyService.getCompanies$(searchedValue)
            : of(null);

          return getDataObs$.pipe(
            finalize(() => {
              this.isSearchingCompanies = false;
            })
          );
        })
      )
      .subscribe({
        next: (x: ICompanySearchResponse) => {
          if (x) {
            this.companyResults = x.companies;
            this.companySearchResults = x.companies.map(
              (p: ICompany) => new AutoCompleteData(p.id.toString(), p.name)
            );
          }
        },
      });

    // People
    this.selectedPersonList = this.peopleControl.value.map(
      (person: IPersonSearch) =>
        new SelectListItem(person.id.toString(), person.name)
    );
    this.peopleControl.valueChanges.pipe(takeUntil(this.destroy$)).subscribe({
      next: (x: IPersonSearch[]) => {
        this.selectedPersonList = x.map(
          (person) => new SelectListItem(person.id.toString(), person.name)
        );
      },
    });

    this.searchPeopleFormControl.valueChanges
      .pipe(
        distinctUntilChanged(),
        debounceTime(500),
        takeUntil(this.destroy$),
        tap(() => {
          this.isSearchingPeople = true;
        }),
        switchMap((searchedValue: string) => {
          if (searchedValue === '') {
            this.resetSearchResults();
          }
          const getDataObs$ = searchedValue
            ? this.peopleAndCompanyService.getPeople$(searchedValue)
            : of(null);

          return getDataObs$.pipe(
            finalize(() => {
              this.isSearchingPeople = false;
            })
          );
        })
      )
      .subscribe({
        next: (x: IPersonSearchResponse) => {
          if (x) {
            this.personResults = x.persons;
            this.personSearchResults = x.persons.map((p: IPersonSearch) => {
              if (
                p.citywireCompanyName === 'Not Assigned to a Company' ||
                p.citywireCompanyName === 'null'
              ) {
                p.citywireCompanyName = '';
              }
              return new AutoCompleteData(
                p.id.toString(),
                p.name + ' (' + p.emailAddress + ') ' + p.citywireCompanyName
              );
            });
          }
        },
      });
  }

  // Topics
  removeTopic($event: ISelectListItem) {
    let index = this.topicControl.value.findIndex(
      (cl) => cl.id.toString() === $event.id
    );
    this.topicControl.removeAt(index);
    this.onItemRemoved($event, SurtitleTagType.Topic);
    this.reportAutoTagRemoval('topic', $event, this.autoAddedTopicIds);

    this.resetSearchResults();
  }

  addTopic($event: ISelectListItem) {
    if (!this.topicControl.value.find((x) => x.id.toString() === $event.id)) {
      let selectedTopicList = this.topicResults.filter(
        (x: ITopic) => x.id.toString() === $event.id
      );

      this.topicControl.push(new FormControl(selectedTopicList[0]));
      this.onItemSelected($event, SurtitleTagType.Topic);
    }
    this.resetSearchResults();
  }

  private filterTopicsByWebsites(
    topics: ITopicWithTranslations[],
    websites: string[]
  ): ITopicWithTranslations[] {
    if (websites.length === 0) {
      return topics;
    }
    return topics.filter((t) => t.channels?.some((c) => websites.includes(c)));
  }

  private removeUnavailableTopics(
    allTopics: ITopicWithTranslations[],
    websites: string[]
  ) {
    if (websites.length === 0) {
      return;
    }
    const removedTopics = this.topicControl.value.filter((selected) => {
      const topic = allTopics.find((t) => t.id === selected.id) ?? selected;
      return !topic.channels?.some((c) => websites.includes(c));
    });
    removedTopics.forEach((t) =>
      this.removeTopic(new SelectListItem(t.id.toString(), t.displayName))
    );
    if (removedTopics.length > 0) {
      this._snackBar.open(
        `Removed topics not available on the selected websites: ${removedTopics
          .map((t) => t.displayName)
          .join(', ')}`,
        null,
        { duration: 5000 }
      );
    }
  }

  // Tags
  removeTags($event: ISelectListItem) {
    let index = this.tagControl.value.findIndex((cl) => cl.id === $event.id);
    this.tagControl.removeAt(index);
    this.onItemRemoved($event, SurtitleTagType.Soft);
    this.reportAutoTagRemoval('tag', $event, this.autoAddedTagIds);

    this.resetSearchResults();
  }

  addTags($event: ISelectListItem) {
    if (!this.tagControl.value.find((x) => x.id === $event.id)) {
      let selectedTagList = this.tagResults.filter((x) => x.id === $event.id);
      this.tagControl.push(new FormControl(selectedTagList[0]));
      this.onItemSelected($event, SurtitleTagType.Soft);
    }
    this.resetSearchResults();
  }

  createSoftTag(tagName: string) {
    this.dialogService
      .openConfirmationDialog({
        title: 'Add new tag?',
        message: `Are you sure you want to add the new tag "${tagName}" to the tag pool?`,
        confirmLabel: 'Ok',
      })
      .afterClosed()
      .pipe(
        takeUntil(this.destroy$),
        filter((dialogResponse) => !!dialogResponse),
        switchMap(() => {
          return this.taggingService.create$(tagName).pipe(
            catchError((x) => {
              this.dialogService.openErrorDialog({
                message: x,
              });
              return of(null);
            })
          );
        })
      )
      .subscribe({
        next: (newTag: ICreateTagResponse) => {
          if (newTag && newTag.tagId != '') {
            this.selectedTagList.push(
              new SelectListItem(newTag.tagId, tagName)
            );
            this.tagControl.push(
              new FormControl(new Tag(newTag.tagId, TagType.Soft, tagName))
            );
            this.resetSearchResults();
            this.searchTagsFormControl.setValue('');
          }
        },
      });
  }

  // Companies
  removeCompanies($event: ISelectListItem) {
    let index = this.companyControl.value.findIndex(
      (cl) => cl.id.toString() === $event.id
    );

    this.handleRemoveLink.emit({
      regex:
        '(<a[^>]*data-company-id="' +
        this.companyControl.value[index].id +
        '"[^>]*>(.*?)</[a|A]>)',
      newValue: '$2',
    });

    this.companyControl.removeAt(index);
    this.onItemRemoved($event, SurtitleTagType.Company);
    this.resetSearchResults();
  }

  addCompanies($event: ISelectListItem) {
    if (!this.companyControl.value.find((x) => x.id.toString() === $event.id)) {
      let selectedCompanyList = this.companyResults.filter(
        (x) => x.id.toString() === $event.id
      );

      this.companyControl.push(new FormControl(selectedCompanyList[0]));
      this.onItemSelected($event, SurtitleTagType.Company);
    }
    this.resetSearchResults();
  }

  addCompanyToContentBody($event: IAutoCompleteData) {
    let companyInfo = this.companyControl.value.find(
      (x) => x.id.toString() === $event.id
    );

    this.handleAddToBody.emit({
      id: companyInfo.id.toString(),
      name: $event.displayName,
      dataLinkType: DataLinkType.COMPANY,
    });
  }

  // People
  removePerson($event: ISelectListItem) {
    let index = this.peopleControl.value.findIndex(
      (cl) => cl.id.toString() === $event.id
    );
    this.peopleControl.removeAt(index);
    this.onItemRemoved($event, SurtitleTagType.Person);

    this.resetSearchResults();
  }

  addPerson($event: ISelectListItem) {
    if (!this.peopleControl.value.find((x) => x.id.toString() === $event.id)) {
      let selectedPersonList = this.personResults.filter(
        (x) => x.id.toString() === $event.id
      );
      this.peopleControl.push(new FormControl(selectedPersonList[0]));
      this.onItemSelected($event, SurtitleTagType.Person);

      let x = selectedPersonList[0];
      if (x.citywireCompanyID) {
        this.companyResults.push(
          new Company(x.citywireCompanyID, x.citywireCompanyName)
        );

        this.addCompanies({
          id: x.citywireCompanyID.toString(),
          displayName: x.citywireCompanyName,
        });
      }
    }
    this.resetSearchResults();
  }

  sendDqaEmail() {
    this.emailService.sendDqaEmail();
  }

  onItemSelected(selectedItem: ISelectListItem, tagType: SurtitleTagType) {
    this.handleItemSelected.emit({ tag: selectedItem, tagType: tagType });
  }

  onItemRemoved(selectedItem: ISelectListItem, tagType: SurtitleTagType) {
    this.handleItemRemoved.emit({ tag: selectedItem, tagType: tagType });
  }

  promoteSurtitle(selectedItem: ISelectListItem, tagType: SurtitleTagType) {
    this.handleSurtitlePromoted.emit({ tag: selectedItem, tagType: tagType });
  }

  resetSearchResults() {
    this.tagSearchResults = [];
    this.companySearchResults = [];
    this.personSearchResults = [];
  }

  // ---- AI Auto-Tagging (CMS-2565) -----------------------------------------

  ngOnChanges(changes: SimpleChanges): void {
    if (!this.isAutoTaggingEnabled) {
      return;
    }
    // Idempotent (same id + object): the status bar's Smart Link button runs auto tagging too.
    this.smartLinkTriggers.register(AUTO_TAG_TRIGGER_ID, this.autoTagTrigger);
    if (!changes['articleId']) {
      return;
    }
    if (!this.articleId) {
      this.autoTaggingState.activateUnsaved();
      return;
    }
    this.autoTaggingState.activate(this.articleId);
  }

  get isAutoTagBusy(): boolean {
    return (
      this.autoTagStatus === 'saving' ||
      this.autoTagStatus === 'triggering' ||
      this.autoTagStatus === 'running'
    );
  }

  get canRunAutoTag(): boolean {
    return (
      !!this.articleId &&
      this.isEditMode &&
      !this.isAutoTagBusy &&
      !this.autoTagGateway?.isSaving()
    );
  }

  get autoTagButtonLabel(): string {
    if (this.autoTagStatus === 'saving') return 'Saving…';
    return this.isAutoTagBusy ? 'Auto tagging…' : 'Auto Tag';
  }

  // Mirrors smart linking's smartLinkButtonTitle so the two buttons explain refusals identically.
  get autoTagButtonTitle(): string {
    if (!this.isEditMode) return AUTO_TAG_READ_ONLY_HINT;
    if (!this.articleId) return AUTO_TAG_SAVE_FIRST_HINT;
    if (this.autoTagGateway?.isSaving()) return AUTO_TAG_SAVE_IN_PROGRESS_HINT;
    if (this.isAutoTagBusy) return AUTO_TAG_ALREADY_RUNNING_HINT;
    return AUTO_TAG_READY_HINT;
  }

  get autoTagErrorMessage(): string {
    if (this.autoTagErrorReason === 'run_failed') {
      return AUTO_TAG_RUN_FAILED_MESSAGE;
    }
    return AUTO_TAG_UNAVAILABLE_MESSAGE;
  }

  get autoTagBusyText(): string {
    return this.autoTagStatus === 'saving' ? 'Saving your content…' : 'Auto tagging…';
  }

  get autoTagBusyDetail(): string {
    return this.autoTagStatus === 'saving'
      ? 'Saving before we suggest tags'
      : 'Reading the content and matching it to the taxonomy';
  }

  async onRunAutoTag(event?: Event): Promise<void> {
    // stopPropagation so the click doesn't toggle the expansion panel it sits in.
    event?.stopPropagation();
    if (!this.canRunAutoTag) return;
    // Expand the panel so the journalist is focused on the results as they land.
    this.sectionBox?.openPanel();
    this.hideNoChangeNotice();

    // Re-running byte-identical content only replays the last result, so tell the journalist
    // nothing changed instead of showing a pointless progress spinner.
    if (await this.isContentUnchangedSinceRun()) {
      this.showNoChangeNotice();
      return;
    }

    this.autoTagReviewItems = [];
    this.autoTaggingState.run(this.autoTagGateway);
  }

  private async isContentUnchangedSinceRun(): Promise<boolean> {
    if (
      this.autoTagStatus !== 'complete' ||
      !this.taggedContentHash ||
      !this.autoTagGateway
    ) {
      return false;
    }
    const currentHash = await autoTagContentHash(this.autoTagGateway.getContent());
    return currentHash === this.taggedContentHash;
  }

  private showNoChangeNotice(): void {
    this.autoTagNoChangeVisible = true;
    this.noChangeTimer = setTimeout(() => {
      this.autoTagNoChangeVisible = false;
      this.noChangeTimer = null;
    }, AUTO_TAG_NO_CHANGE_DISPLAY_MS);
  }

  private hideNoChangeNotice(): void {
    if (this.noChangeTimer) {
      clearTimeout(this.noChangeTimer);
      this.noChangeTimer = null;
    }
    this.autoTagNoChangeVisible = false;
  }

  // Pick one of an ambiguous suggestion's options: apply it (unless "skip - add manually",
  // id === null) and clear the item from the review list.
  pickReviewOption(item: IAutoTagReview, option: IAutoTagReviewOption): void {
    if (option.id !== null) {
      this.applyItem(item.kind, option.id, option.name, option.suggestionKey);
      this.autoTaggingState.reportDecisions([
        {
          kind: item.kind,
          id: option.id,
          name: option.name,
          action: 'accepted',
          source: 'review',
          suggestionKey: option.suggestionKey,
        },
      ]);
    } else if (option.suggestionKey) {
      // Explicit skip: reject the suggestion so removals/rejections are recorded like accepts.
      this.autoTaggingState.reportDecisions([
        {
          kind: item.kind,
          id: '',
          name: item.name,
          action: 'rejected',
          suggestionKey: option.suggestionKey,
        },
      ]);
    }
    this.autoTagReviewItems = this.autoTagReviewItems.filter((r) => r !== item);
  }

  private onAutoTagState(state: IAutoTagRunState): void {
    this.autoTagStatus = state.status;
    this.autoTagErrorReason = state.errorReason;

    // Durable "added by AI" icon from the persisted links (independent of any run). Runs on every
    // state emission, so it re-applies when the links load and when the controls (re)populate.
    this.markLinkedAsAutoTagged(state.linked);

    // Only a completed run has a content hash to compare the edit buffer against; any other status
    // (a new run starting, idle, error) clears the stale-content warning.
    if (state.status === 'complete') {
      this.taggedContentHash = state.contentHash;
      void this.refreshContentChanged();
    } else {
      this.taggedContentHash = null;
      this.autoTagContentChanged = false;
    }

    if (
      state.status === 'complete' &&
      state.result &&
      state.runId &&
      state.runId !== this.autoAppliedRunId
    ) {
      this.autoAppliedRunId = state.runId;
      if (state.restored) {
        // Rehydrated on open: the tags are already saved on the article, so only re-flag the ones
        // this run added (the AI icon) - never re-add them or re-report acceptance on every reload.
        this.markResultAsAutoTagged(state.result);
        return;
      }
      // Confident (cms-api's >=0.8 `applied`) auto-apply; ambiguous ones go to the review list
      // shown at the top of the panel for the journalist to resolve.
      state.result.applied.forEach((item) =>
        this.applyItem(item.kind, item.id, item.name, item.suggestionKey),
      );
      this.autoTagReviewItems = state.result.review ?? [];
      // Tell the backend which confident suggestions were auto-accepted (so removals can be
      // measured against them).
      const accepted: IAutoTagDecision[] = state.result.applied.map((item) => ({
        kind: item.kind,
        id: item.id,
        name: item.name,
        action: 'accepted',
        source: 'confident',
        suggestionKey: item.suggestionKey,
      }));
      this.autoTaggingState.reportDecisions(accepted);
    }
  }

  // Recompute whether the live content has diverged from the completed run's content hash. Runs on
  // content edits (debounced) and when a run completes. No completed run to compare against -> the
  // warning is off.
  private async refreshContentChanged(): Promise<void> {
    if (!this.taggedContentHash || !this.autoTagGateway) {
      this.autoTagContentChanged = false;
      return;
    }
    const currentHash = await autoTagContentHash(this.autoTagGateway.getContent());
    this.autoTagContentChanged =
      !!this.taggedContentHash && currentHash !== this.taggedContentHash;
  }

  // Record the ids a restored run suggested - confident `applied` plus each review item's options
  // (only the option the journalist picked is ever saved) - so search-list shows the AI icon again
  // after a reload. Ids go into the auto-added sets unconditionally rather than only for items
  // currently in the controls: the saved topics/tags may still be loading when the run is restored,
  // and buildTopicItem/buildTagItem re-applies the flag on every (re)build, so the icon survives
  // whichever resolves first. buildItem only flags items actually in the controls, so a suggested id
  // that was never saved is harmless. Never re-adds items or re-reports acceptance.
  private markResultAsAutoTagged(result: IAutoTagResult): void {
    const collect = (
      kind: AutoTagKind,
      id: string | null,
      suggestionKey?: string,
    ) => {
      if (id === null) return;
      if (kind === 'topic') this.autoAddedTopicIds.add(id);
      else if (kind === 'tag') this.autoAddedTagIds.add(id);
      this.rememberSuggestionKey(kind, id, suggestionKey);
    };
    result.applied.forEach((item) =>
      collect(item.kind, item.id, item.suggestionKey),
    );
    result.review.forEach((item) =>
      item.options.forEach((option) =>
        collect(item.kind, option.id, option.suggestionKey),
      ),
    );

    this.selectedTopicList = this.topicControl.value.map((topic) =>
      this.buildTopicItem(topic),
    );
    this.selectedTagList = this.tagControl.value.map((tag) =>
      this.buildTagItem(tag),
    );
  }

  // Flag the saved topics/tags recorded as AI-linked in the shared links table so search-list shows
  // the AI icon. This is the durable source (persisted links, not the run), so it survives reload,
  // content edits and run expiry. Ids are added unconditionally; buildTopicItem/buildTagItem apply
  // the flag on every (re)build, so it holds even if the controls populate after the links load.
  private markLinkedAsAutoTagged(linked: IAutoTagLink[]): void {
    if (!linked || !linked.length) {
      return;
    }
    linked.forEach((link) => {
      if (link.kind === 'topic') {
        this.autoAddedTopicIds.add(link.id);
      } else if (link.kind === 'tag') {
        this.autoAddedTagIds.add(link.id);
      }
    });
    this.selectedTopicList = this.topicControl.value.map((topic) =>
      this.buildTopicItem(topic),
    );
    this.selectedTagList = this.tagControl.value.map((tag) =>
      this.buildTagItem(tag),
    );
  }

  private applyItem(
    kind: AutoTagKind,
    id: string,
    name: string,
    suggestionKey?: string,
  ): void {
    if (kind === 'topic') {
      if (this.topicControl.value.some((t) => t?.id?.toString() === id)) {
        return; // already present (manual or a prior run) - don't duplicate
      }
      this.autoAddedTopicIds.add(id);
      this.rememberSuggestionKey(kind, id, suggestionKey);
      this.topicControl.push(
        new FormControl(new TopicWithTranslations(Number(id), name, name, [], [], []))
      );
      this.topicControl.markAsDirty();
      this.onItemSelected(new SelectListItem(id, name), SurtitleTagType.Topic);
    } else if (kind === 'tag') {
      if (this.tagControl.value.some((t) => t?.id === id)) {
        return;
      }
      this.autoAddedTagIds.add(id);
      this.rememberSuggestionKey(kind, id, suggestionKey);
      this.tagControl.push(new FormControl(new Tag(id, TagType.Soft, name, true)));
      this.tagControl.markAsDirty();
      this.onItemSelected(new SelectListItem(id, name), SurtitleTagType.Soft);
    }
  }

  private rememberSuggestionKey(
    kind: AutoTagKind,
    id: string | null,
    suggestionKey?: string,
  ): void {
    if (id === null || !suggestionKey) {
      return;
    }
    this.autoAddedSuggestionKeys.set(`${kind}:${id}`, suggestionKey);
  }

  // Build the display items, flagging the ones auto-tagging added so search-list shows the AI icon.
  private buildTopicItem(topic: ITopic | ITopicWithTranslations): SelectListItem {
    const item = new SelectListItem(topic.id.toString(), topic.displayName);
    item.isAutoTagged = this.autoAddedTopicIds.has(topic.id.toString());
    return item;
  }

  private buildTagItem(tag: ITag): SelectListItem {
    const item = new SelectListItem(tag.id, tag.text);
    item.isAutoTagged = this.autoAddedTagIds.has(tag.id);
    return item;
  }

  // When a journalist removes an item auto-tagging added, tell the backend (so removal rates can
  // be tracked) and forget the marker so a later manual re-add shows no icon and re-reports nothing.
  private reportAutoTagRemoval(
    kind: AutoTagKind,
    item: ISelectListItem,
    autoAddedIds: Set<string>,
  ): void {
    if (!this.isAutoTaggingEnabled || !autoAddedIds.has(item.id)) {
      return;
    }
    autoAddedIds.delete(item.id);
    const mapKey = `${kind}:${item.id}`;
    const suggestionKey = this.autoAddedSuggestionKeys.get(mapKey);
    this.autoAddedSuggestionKeys.delete(mapKey);
    this.autoTaggingState.reportDecisions([
      { kind, id: item.id, name: item.displayName, action: 'removed', suggestionKey },
    ]);
  }

  ngOnDestroy(): void {
    if (this.isAutoTaggingEnabled) {
      this.smartLinkTriggers.unregister(AUTO_TAG_TRIGGER_ID, this.autoTagTrigger);
      this.autoTaggingState.deactivate();
    }
    this.hideNoChangeNotice();
    this.destroy$.next(true);
    this.destroy$.unsubscribe();
  }
}
